import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { observer } from 'mobx-react-lite';

import FilterIcon from 'assets/icons/filter.svg';
import FilterFilledIcon from 'assets/icons/filter-filled.svg';
import { Button, CopyButton, DataView } from 'components';
import { useClosePosition } from 'hooks/meme/useClosePosition';
import { store } from 'store';
import { cutAddress } from 'utils/helpers';
import { Address } from 'viem';

import * as S from '../common.styles';

export const OpenPositions = observer(() => {
  const { portfolioResponse, positions, updatePortfolio } = store.portfolioStore;
  const isLoading = portfolioResponse.isLoading && !portfolioResponse.data;

  const closePosition = useClosePosition();

  const [selectedLeader, setSelectedLeader] = useState<Address | null>(null);
  const [closingLeader, setClosingLeader] = useState<Address | null>(null);
  const [closingPositionId, setClosingPositionId] = useState<string | null>(null);

  const selectedLeaderPositions = useMemo(
    () => positions.filter((pos) => pos.leader === selectedLeader),
    [positions, selectedLeader]
  );

  const closeAllPositionsForLeader = async (leader: Address) => {
    const leaderPositions = positions.filter((pos) => pos.leader === leader);
    if (!leaderPositions.length) return;

    setClosingLeader(leader);

    let successCount = 0;

    for (const pos of leaderPositions) {
      const result = await closePosition({
        leaderAddress: pos.leader,
        conditionId: pos.conditionId,
        asset: pos.asset,
      });

      if (result?.closed) {
        successCount += 1;
      }
    }

    setClosingLeader(null);

    if (successCount > 0) {
      toast.success(
        successCount === leaderPositions.length
          ? `${successCount} positions closed`
          : `${successCount} of ${leaderPositions.length} positions closed`
      );
      updatePortfolio();
      return;
    }

    toast.error('Failed to close positions');
  };

  const closeSinglePosition = async (positionId: string) => {
    const pos = positions.find((item) => item.id === positionId);
    if (!pos) return;

    setClosingPositionId(positionId);

    const result = await closePosition({
      leaderAddress: pos.leader,
      conditionId: pos.conditionId,
      asset: pos.asset,
    });

    setClosingPositionId(null);

    if (result?.closed) {
      toast.success('Position closed');
      updatePortfolio();
      return;
    }

    toast.error('Failed to close position');
  };

  if (isLoading) {
    return (
      <S.EmptyState>
        <div>LOADING...</div>
        <div>Loading positions...</div>
      </S.EmptyState>
    );
  }

  if (positions.length === 0) {
    return (
      <S.EmptyState>
        <div>// NO DATA</div>
        <div>No open positions</div>
      </S.EmptyState>
    );
  }

  return (
    <div>
      {selectedLeader && (
        <S.PositionsHeader>
          <span>Selected leader positions:</span>
          <S.PositionsButtons>
            <Button
              size="small"
              color="grey"
              onClick={() => closeAllPositionsForLeader(selectedLeader)}
              disabled={closingLeader === selectedLeader || selectedLeaderPositions.length === 0}
            >
              {closingLeader === selectedLeader ? 'Closing...' : 'Close All'}
            </Button>
          </S.PositionsButtons>
        </S.PositionsHeader>
      )}

      <S.DesktopOnlyTableWrapper>
        <S.Table>
          <thead>
            <tr>
              <th>Market</th>
              <th>Leader</th>
              <th style={{ textAlign: 'right' }}>Invested</th>
              <th style={{ textAlign: 'right' }}>Current</th>
              <th style={{ textAlign: 'right' }}>PnL</th>
              <th style={{ textAlign: 'right' }}>ROI</th>
              <th style={{ textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((pos) => (
              <S.Tr key={pos.id} hidden={!!selectedLeader && pos.leader !== selectedLeader}>
                <td>
                  <S.TokenCell>
                    {pos.icon && <S.MarketIcon src={pos.icon} alt="" loading="lazy" />}
                    <S.Symbol>{pos.title}</S.Symbol>
                  </S.TokenCell>
                  <div style={{ fontSize: '12px', color: 'rgba(120, 160, 190, 0.7)' }}>
                    {pos.outcome}
                  </div>
                </td>
                <td>
                  <S.Address>
                    {pos.leaderProfileImage && (
                      <S.EntityIcon src={pos.leaderProfileImage} alt="" loading="lazy" />
                    )}
                    <S.AddressLink
                      href={`https://polygonscan.com/address/${pos.leader}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {pos.leaderName || cutAddress(pos.leader)}
                    </S.AddressLink>
                    <CopyButton copyText={pos.leader} />
                    <S.FilterButton
                      $isActive={selectedLeader === pos.leader}
                      onClick={() => setSelectedLeader(selectedLeader === pos.leader ? null : pos.leader)}
                    >
                      {selectedLeader === pos.leader ? <FilterFilledIcon /> : <FilterIcon />}
                    </S.FilterButton>
                  </S.Address>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <DataView value={pos.initialValue} type="dollars" toFixed={2} />
                </td>
                <td style={{ textAlign: 'right' }}>
                  <DataView value={pos.currentValue} type="dollars" toFixed={2} />
                </td>
                <td style={{ textAlign: 'right' }}>
                  <DataView value={pos.totalPnl} hasSign hasColor type="dollars" toFixed={2} />
                </td>
                <td style={{ textAlign: 'right' }}>
                  <DataView value={pos.roi} hasSign hasColor type="percent" toFixed={1} />
                </td>
                <td style={{ textAlign: 'right' }}>
                  <S.ActionButton
                    $variant="danger"
                    onClick={() => closeSinglePosition(pos.id)}
                    disabled={closingLeader === pos.leader || closingPositionId === pos.id}
                  >
                    {closingPositionId === pos.id ? 'Closing...' : 'Close'}
                  </S.ActionButton>
                </td>
              </S.Tr>
            ))}
          </tbody>
        </S.Table>
      </S.DesktopOnlyTableWrapper>

      <S.MobileCards>
        {positions
          .filter((pos) => !selectedLeader || pos.leader === selectedLeader)
          .map((pos) => (
            <S.MobileCard key={pos.id}>
              <S.MobileCardHeader>
                <S.MobileCardTitle>
                  <S.TokenCell>
                    {pos.icon && <S.MarketIcon src={pos.icon} alt="" loading="lazy" />}
                    <S.Symbol>{pos.title}</S.Symbol>
                  </S.TokenCell>
                </S.MobileCardTitle>
                <S.MobileCardSubtitle>{pos.outcome}</S.MobileCardSubtitle>
                <S.Address>
                  {pos.leaderProfileImage && (
                    <S.EntityIcon src={pos.leaderProfileImage} alt="" loading="lazy" />
                  )}
                  <S.AddressLink
                    href={`https://polygonscan.com/address/${pos.leader}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {pos.leaderName || cutAddress(pos.leader)}
                  </S.AddressLink>
                  <CopyButton copyText={pos.leader} />
                  <S.FilterButton
                    $isActive={selectedLeader === pos.leader}
                    onClick={() => setSelectedLeader(selectedLeader === pos.leader ? null : pos.leader)}
                  >
                    {selectedLeader === pos.leader ? <FilterFilledIcon /> : <FilterIcon />}
                  </S.FilterButton>
                </S.Address>
              </S.MobileCardHeader>

              <S.MobileStatsGrid>
                <S.MobileStat>
                  <S.MobileStatLabel>Invested</S.MobileStatLabel>
                  <S.MobileStatValue>
                    <DataView value={pos.initialValue} type="dollars" toFixed={2} />
                  </S.MobileStatValue>
                </S.MobileStat>
                <S.MobileStat>
                  <S.MobileStatLabel>Current</S.MobileStatLabel>
                  <S.MobileStatValue>
                    <DataView value={pos.currentValue} type="dollars" toFixed={2} />
                  </S.MobileStatValue>
                </S.MobileStat>
                <S.MobileStat>
                  <S.MobileStatLabel>PnL</S.MobileStatLabel>
                  <S.MobileStatValue>
                    <DataView value={pos.totalPnl} hasSign hasColor type="dollars" toFixed={2} />
                  </S.MobileStatValue>
                </S.MobileStat>
                <S.MobileStat>
                  <S.MobileStatLabel>ROI</S.MobileStatLabel>
                  <S.MobileStatValue>
                    <DataView value={pos.roi} hasSign hasColor type="percent" toFixed={1} />
                  </S.MobileStatValue>
                </S.MobileStat>
              </S.MobileStatsGrid>

              <S.ActionButton
                $variant="danger"
                onClick={() => closeSinglePosition(pos.id)}
                disabled={closingLeader === pos.leader || closingPositionId === pos.id}
              >
                {closingPositionId === pos.id ? 'Closing...' : 'Close'}
              </S.ActionButton>
            </S.MobileCard>
          ))}
      </S.MobileCards>
    </div>
  );
});
